import { Box, Chip, Typography } from "@mui/material";
import {
  BooleanField,
  Button,
  Confirm,
  Datagrid,
  DeleteWithConfirmButton,
  EmailField,
  FunctionField,
  NumberField,
  ReferenceField,
  ReferenceManyField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useNotify,
  useRecordContext,
  useRefresh,
  usePermissions,
  useShowController,
  useGetMany,
} from "react-admin";
import { Archive, Delete, RestartAlt } from "@mui/icons-material";
import { useState } from "react";
import ApiClient from "../../ApiClient";
import API from "../../Routes";
import { isInternalAdmin, isSuperAdmin } from "../../auth-provider";
import { ResultShow } from "./result-show";

const isArchived = (record) => record?.archived === true;

const STAGE = process.env.REACT_APP_STAGE || "staging";
const EMPLOYEE_ENDPOINT =
  STAGE === "production"
    ? "https://app.thrivetech.co/employees"
    : "https://app.chasana.biz/employees";

const ProjectShowTitle = () => {
  const record = useRecordContext();
  return <span>Project {record ? `"${record.name}"` : ""}</span>;
};

const SubReference = ({ translateChoice, children, ...props }) => (
  <ReferenceField {...props}>{children}</ReferenceField>
);

const ArchiveButton = ({ record }) => {
  const [open, setOpen] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  if (isArchived(record)) {
    return null;
  }

  return (
    <>
      <Button label="Archive" onClick={() => setOpen(true)} color="error">
        <Archive />
      </Button>
      <Confirm
        isOpen={open}
        title="Archive project"
        content={`Are you sure you want to archive project ${record?.name} (${record?.id})?`}
        onConfirm={async () => {
          setOpen(false);
          try {
            await ApiClient.fetch(API.PROJECTS.ARCHIVE(record.id), {
              method: "POST",
            });
            notify(`Project ${record?.id} archived`);
            refresh();
          } catch (error) {
            notify(
              `Failed to archive project ${record?.id}: ${
                error?.message || error
              }`,
              { type: "error" },
            );
          }
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const RestoreButton = ({ record }) => {
  const [open, setOpen] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  if (!isArchived(record)) {
    return null;
  }

  return (
    <>
      <Button label="Restore" onClick={() => setOpen(true)} color="primary">
        <RestartAlt />
      </Button>
      <Confirm
        isOpen={open}
        title="Restore project"
        content={`Are you sure you want to restore project ${record?.name} (${record?.id})?`}
        onConfirm={async () => {
          setOpen(false);
          try {
            await ApiClient.fetch(API.PROJECTS.RESTORE(record.id), {
              method: "POST",
            });
            notify(`Project ${record?.id} restored`);
            refresh();
          } catch (error) {
            notify(
              `Failed to restore project ${record?.id}: ${
                error?.message || error
              }`,
              { type: "error" },
            );
          }
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const ProjectShowActions = (props) => {
  const { record } = useShowController();

  return (
    <TopToolbar {...props}>
      <ArchiveButton record={record} />
      <RestoreButton record={record} />
      <DeleteWithConfirmButton record={record} />
    </TopToolbar>
  );
};

const EmployeeGDPRErasureButton = ({ record }) => {
  const [open, setOpen] = useState(false);
  const notify = useNotify();
  const employeeId = record?.employeeId;

  if (!employeeId) {
    return null;
  }

  return (
    <>
      <Button
        label="GDPR DELETION"
        onClick={() => setOpen(true)}
        color="error"
        size="small"
      >
        <Delete />
      </Button>
      <Confirm
        isOpen={open}
        title="Delete employee data"
        content={`Are you sure you want to permanently erase GDPR data for employee ${employeeId}?`}
        onConfirm={async () => {
          setOpen(false);

          try {
            await ApiClient.fetch(API.EMPLOYEES.GDPR_ERASURE(employeeId), {
              method: "POST",
            });
            notify(
              `Employee ${employeeId} GDPR data has been deleted successfully`,
            );
          } catch (error) {
            notify(
              `Failed to trigger GDPR deletion for employee ${employeeId}: ${
                error?.message || error
              }`,
              { type: "error" },
            );
          }
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const TeamsTab = ({ projectId }) => {
  const {
    data: teams,
    isLoading,
    error,
  } = useGetMany("projectTeams", { ids: projectId ? [projectId] : [] });

  if (isLoading) return <span>Loading teams...</span>;
  if (error) return <span>Error loading teams</span>;
  if (!teams || teams.length === 0) return <span>No teams found.</span>;

  return (
    <Datagrid
      data={teams}
      bulkActionButtons={false}
      rowClick={(_id, _resource, record) => `/teams/${record.id}/show`}
    >
      <TextField source="id" />
      <TextField source="name" />
      <TextField source="jobFunction" />
      <BooleanField source="archived" />
    </Datagrid>
  );
};

export const ProjectShow = ({ ...props }) => {
  const permissions = usePermissions();
  const { record } = useShowController();

  return (
    <Show
      {...props}
      title={<ProjectShowTitle />}
      actions={
        isInternalAdmin(permissions.permissions) ? <ProjectShowActions /> : null
      }
    >
      <TabbedShowLayout>
        <Tab label="Employee - Scores">
          <ReferenceManyField
            label={false}
            reference="results"
            target="projectIds"
            filter={{ includeDeleted: true, projectIds: record?.id }}
            perPage={200}
          >
            <Datagrid expand={<ResultShow />} bulkActionButtons={false}>
              <TextField source="employeeId" label="Employee Id" />
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="Email"
                link={false}
              >
                <EmailField source="email" />
              </ReferenceField>
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="External Correlation Id"
                link={false}
              >
                <TextField source="externalCorrelationId" />
              </ReferenceField>
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="Name"
                link={false}
              >
                <FunctionField
                  render={(record) => `${record.name} ${record.lastName}`}
                />
              </ReferenceField>
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="Deleted"
                link={false}
              >
                <BooleanField source="deleted" />
              </ReferenceField>
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="Team"
                link={false}
              >
                <SubReference source="teamId" reference="teams" link={false}>
                  <TextField source="name" />
                </SubReference>
              </ReferenceField>
              <NumberField source="overallCompetencyScore" />
              <ReferenceField
                source="employeeId"
                reference="employees"
                label="Employee Link"
                link={false}
              >
                <FunctionField
                  render={(record) => (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`${EMPLOYEE_ENDPOINT}/${record.id}?token=${record.token}`}
                    >
                      Employee Link
                    </a>
                  )}
                />
              </ReferenceField>
              {isSuperAdmin(permissions.permissions) && (
                <FunctionField
                  label="GDPR Deletion"
                  render={(record) => (
                    <EmployeeGDPRErasureButton record={record} />
                  )}
                />
              )}
            </Datagrid>
          </ReferenceManyField>
        </Tab>
        <Tab label="Teams">{record && <TeamsTab projectId={record.id} />}</Tab>
        <Tab label="Summary">
          <TextField source="id" />
          <TextField source="name" />
          <Box sx={{ marginBottom: "8px" }}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              gutterBottom
              sx={{ fontSize: "12px", marginBottom: "8px" }}
            >
              Status
            </Typography>
            <Chip label={record?.status} />
          </Box>
          <BooleanField source="archived" />
          <ReferenceField
            source="organisationId"
            reference="organisations"
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField
            source="createdBy"
            reference="users"
            link={isInternalAdmin(permissions.permissions) ? "show" : false}
          >
            <TextField source="firstName" />
          </ReferenceField>
          <NumberField source="amountOfEmployees" />
          <TextField source="duration" />
          <TextField source="timezone" />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
